import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { agentManagementKeys } from '~/lib/query-keys';

export interface ManagedAgent {
  id: string;
  name: string;
  description: string;
  active: boolean;
  available: boolean;
  isConfigured?: boolean;
  capabilities?: Record<string, unknown>;
}

interface AgentsListResponse {
  [key: string]: ManagedAgent | unknown;
}

function normalizeAgents(res: AgentsListResponse | ManagedAgent[]): ManagedAgent[] {
  if (Array.isArray(res)) return res as ManagedAgent[];
  if (!res || typeof res !== 'object') return [];
  return Object.values(res).filter(
    (v): v is ManagedAgent =>
      !!v && typeof v === 'object' && typeof (v as ManagedAgent).id === 'string'
  );
}

/**
 * Returns agents that are platform-active **and** available to this tenant.
 * Inactive/unavailable agents are deliberately filtered out — they're a
 * platform-admin concern, not surfaced in Eddy's settings UI.
 *
 * Sources from the ungated `/api/agents` roster, NOT `/api/admin/agents`:
 * the admin proxy passes `includeInactive=true`, which integrations only
 * allows for kb:admin/kb:superadmin — so non-admins got a 403 and the whole
 * self-service roster (incl. their personal "On for me" toggles) failed to
 * load (FRONT-291). `/api/agents` returns the same AgentInfo shape minus the
 * inactive entries, which the `active && available` filter below drops anyway,
 * so the admin view is unchanged.
 *
 * Eddy-internal routing enable/disable is handled separately by
 * `useEnabledAgentIds()` + `useToggleEddyEnabled()`.
 *
 * `enabled` lets an always-mounted caller (the command palette, FRONT-2436) read
 * this list only while it is actually searching; the cache key is unchanged.
 */
export function useAgentManagement(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: agentManagementKeys.list(),
    enabled: options?.enabled,
    queryFn: async (): Promise<ManagedAgent[]> => {
      const res = await api.get<AgentsListResponse | ManagedAgent[]>('/api/agents');
      return normalizeAgents(res).filter((a) => a.active && a.available);
    },
  });
}

/**
 * The current tenant's `tenant_settings.enabled_agent_ids` list, or `null`
 * meaning "all available agents are enabled" (legacy default semantic
 * preserved from when this lived in `enabled-agents.json`).
 */
export function useEnabledAgentIds(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['agent-management', 'enabled'],
    enabled: options?.enabled,
    queryFn: async (): Promise<string[] | null> => {
      const res = await api.get<{ enabledAgents: string[] | null }>('/api/enabled-agents');
      return res.enabledAgents;
    },
  });
}

/**
 * Toggle whether Eddy routes to a specific agent. Single-toggle UX —
 * platform-level `active`/`available` are never touched here.
 *
 * Reads the current enabled list, mutates locally, sends the full new
 * array back. `null` (all-enabled) is materialized into a concrete list
 * the first time the user makes a non-default choice.
 */
export function useToggleEddyEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentId,
      enabled,
      currentEnabled,
      allAvailable,
    }: {
      agentId: string;
      enabled: boolean;
      currentEnabled: string[] | null;
      allAvailable: string[];
    }) => {
      // Materialize null → "all available agents" so we can toggle one off.
      const base = currentEnabled ?? allAvailable;
      const next = enabled ? Array.from(new Set([...base, agentId])) : base.filter((id) => id !== agentId);
      return api.put<{ ok: boolean; enabledAgents: string[] }>('/api/enabled-agents', { agentIds: next });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-management'] });
    },
  });
}

/**
 * Set the entire enabled-agent list in one request. Powers the
 * enable-all / disable-all button: pass every available id to enable
 * all, or an empty array to disable all.
 */
export function useSetEnabledAgents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentIds: string[]) =>
      api.put<{ ok: boolean; enabledAgents: string[] }>('/api/enabled-agents', { agentIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-management'] });
    },
  });
}

/**
 * FRONT-145 Phase 5: this user's personal agent opt-outs — the agent ids
 * they've switched off for themselves. Open to all authenticated users;
 * empty array = everything the admin enabled is on (the on-by-default rule).
 * The agent router subtracts this set from the tenant-enabled list at routing
 * time.
 */
export function useMyDisabledAgents(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['agent-management', 'my-prefs'],
    enabled: options?.enabled,
    queryFn: async (): Promise<string[]> => {
      const res = await api.get<{ disabledAgents: string[] }>('/api/my/agent-preferences');
      return res.disabledAgents ?? [];
    },
  });
}

/**
 * Toggle a single agent on/off for the current user only. `enabled=false`
 * adds it to their personal opt-out list; `enabled=true` removes it. Never
 * touches tenant availability — that's the admin-only `useToggleEddyEnabled`.
 */
export function useToggleMyAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ agentId, enabled }: { agentId: string; enabled: boolean }) =>
      api.put<{ ok: boolean; disabledAgents: string[] }>('/api/my/agent-preferences', {
        agentId,
        enabled,
      }),
    // FRONT-2406 round 2 review: write the PUT response straight into the
    // cache instead of invalidating. An invalidate-triggered refetch can
    // land on a different backend replica whose 60s in-process cache
    // (src/agents/user-agent-prefs-store.ts) is still stale for up to a
    // minute, which would snap the switch the person just flipped back to
    // its old state moments after the click. The PUT response IS the
    // post-write truth — the write clears ITS OWN replica's cache entry
    // before the response goes out — so writing it directly can never be
    // stale the way a follow-up GET can.
    onSuccess: (res) => {
      qc.setQueryData(['agent-management', 'my-prefs'], res.disabledAgents ?? []);
    },
  });
}

/**
 * FRONT-2406 round 2: toggle SEVERAL agents on/off for the current user in
 * ONE request — the Agents page's per-tab "All on for me" master switch.
 * Same cache-write-not-invalidate reasoning as `useToggleMyAgent` above (the
 * two mutate the same server-side denylist and both write straight into the
 * same query's cache), so row switches and the master switch agree the
 * instant either mutation resolves, not just once a refetch happens to land.
 */
export function useSetMyAgentsEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ agentIds, enabled }: { agentIds: string[]; enabled: boolean }) =>
      api.put<{ ok: boolean; disabledAgents: string[] }>('/api/my/agent-preferences', {
        agentIds,
        enabled,
      }),
    onSuccess: (res) => {
      qc.setQueryData(['agent-management', 'my-prefs'], res.disabledAgents ?? []);
    },
  });
}
